/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import toast from "react-hot-toast";
import { CompanyService } from "services/companyService";
import AccountingStatement5Service from "services/accounting-statement-5";
import { formatVndIntegerValue } from "utils";
import DataTable from "./DataTable/index";
import ActionCell from "./components/ActionCell/index";

const currentYear = String(new Date().getFullYear());
const currentMonth = String(new Date().getMonth() + 1);

const tableData = {
  columns: [
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "Mã TK nguồn", accessor: "accountCode", align: "center" },
    { Header: "Tổng chi phí", accessor: "totalCreditAmount", align: "right" },
    { Header: "Thao tác", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

function AccountingTable5() {
  const [companyId, setCompanyId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [totals, setTotals] = useState({ totalCreditAmount: 0 });
  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    month: currentMonth,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
      setCompanyData(payload);
    };
    init();
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!companyId) return;
    try {
      setAccountsLoading(true);
      const response = await AccountingStatement5Service.findAccounts({
        company: companyId,
      });
      const payload = response?.data || response || {};
      const items = payload.items || [];
      setAccounts(items);

      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [companyId, filters.accountCode]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchData = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const response = await AccountingStatement5Service.findAll({
        company: companyId,
        year: query.year,
        month: query.month,
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        accountCode: query.accountCode || undefined,
        searchBy: query.searchBy || undefined,
        page: paginationData.page,
        limit: paginationData.size,
      });
      const payload = response?.data || response || {};
      
      setPaginationData((prev) => ({
        ...prev,
        count: payload.paginate?.count || 0,
        size: payload.paginate?.size || prev.size,
      }));

      const rows = (payload.items || []).map((item) => {
        return {
          voucherNumber: item.rows?.[0]?.voucherNumber || "Nhiều số",
          voucherDate: item.rows?.[0]?.voucherDate || "",
          description: `Bảng kê số 5 - Tháng ${item.month}/${item.year}`,
          accountCode: item.accountCode || "",
          totalCreditAmount: formatVndIntegerValue(item.totals?.totalCreditAmount || 0),
          action: <ActionCell item={{ viewLink: `/templates/accounting-table-5/detail/${item._id}` }} />,
        };
      });

      setDataTableState((prev) => ({ ...prev, rows }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Bảng kê số 5");
      setDataTableState((prev) => ({ ...prev, rows: [] }));
    } finally {
      setLoading(false);
    }
  }, [companyId, query, paginationData.page, paginationData.size]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year || !filters.month) {
      toast.error("Thiếu thông tin công ty, năm hoặc tháng");
      return;
    }
    try {
      setSyncing(true);
      
      // 1. Preview data
      const previewRes = await AccountingStatement5Service.preview({
        company: companyId,
        year: filters.year,
        month: filters.month,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        accountCode: filters.accountCode,
      });
      const previewData = previewRes?.data || previewRes || {};

      // 2. Sync to DB
      await AccountingStatement5Service.sync({
        company: companyId,
        unit: companyData?.unitName || companyData?.name || "Đơn vị",
        address: companyData?.address || "",
        year: filters.year,
        month: filters.month,
        accountCode: filters.accountCode,
        debitAccountLabels: previewData.debitAccountLabels,
        rows: previewData.rows,
        totals: previewData.totals,
      });

      toast.success("Đồng bộ dữ liệu Bảng kê số 5 thành công");
      setQuery((prev) => ({ ...prev, ...filters }));
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            onSync={handleSync}
            onFetchData={(newFilters) => {
              setQuery((prev) => ({ ...prev, ...newFilters }));
              setPaginationData((prev) => ({ ...prev, page: 1 }));
            }}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingTable5;
